import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Guardian Score circular gauge (SVG).
 *
 * Designed to slot inside <GuardianPortfolioPanel leadingSlot=...> on the
 * Executive Dashboard. Renders a 220 × 220 dial with:
 *   - a soft track (Off White at 8 % opacity over Deep Navy)
 *   - a progress arc whose hue maps to the band (excellent/good/caution/at_risk)
 *   - the score number + band label + components hint inside the ring
 *
 * Bands match the backend formula in /api/dashboard/executive-status :
 *   excellent ≥85 · good ≥70 · caution ≥50 · at_risk <50
 */
const BAND_TO_COLOR = {
  excellent: "#22C55E",
  good:      "#1677FF",
  caution:   "#FBBF24",
  at_risk:   "#FF6B7A",
};

function pathForArc(cx, cy, radius, startAngleDeg, endAngleDeg) {
  // Returns the SVG `d` for a clockwise arc between the two angles.
  const start = ((startAngleDeg - 90) * Math.PI) / 180;
  const end = ((endAngleDeg - 90) * Math.PI) / 180;
  const x1 = cx + radius * Math.cos(start);
  const y1 = cy + radius * Math.sin(start);
  const x2 = cx + radius * Math.cos(end);
  const y2 = cy + radius * Math.sin(end);
  const largeArc = endAngleDeg - startAngleDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
}

export default function GuardianScoreGauge({
  value = 0,
  band = "good",
  size = 220,
  components,
  testId = "guardian-score-gauge",
}) {
  const { t } = useTranslation("dashboard");
  const clamped = Math.max(0, Math.min(100, value));
  const color = BAND_TO_COLOR[band] || BAND_TO_COLOR.good;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 14;
  // Open-ended dial: from 135° → 405° (270° sweep). Score 0..100 maps to 0..270.
  const startAngle = 135;
  const endAngle = 135 + 270;
  const valueAngle = startAngle + (clamped / 100) * 270;

  return (
    <div data-testid={testId} className="flex flex-col items-center w-full">
      {/* Fixed-size relative box so the center label overlay below stays
          perfectly centered on the ring at ANY `size` — this gauge is reused
          at multiple sizes (dashboard panel, Dashboard hero), and a fixed-px
          negative-margin trick here would only line up at one specific size. */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={`Guardian Score ${clamped}/100`}
          className="absolute inset-0"
        >
          {/* Track */}
          <path
            d={pathForArc(cx, cy, radius, startAngle, endAngle)}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="14"
            strokeLinecap="round"
          />
          {/* Tick marks */}
          {Array.from({ length: 11 }).map((_, i) => {
            const a = startAngle + (i / 10) * 270;
            const rad = ((a - 90) * Math.PI) / 180;
            const x1 = cx + (radius - 18) * Math.cos(rad);
            const y1 = cy + (radius - 18) * Math.sin(rad);
            const x2 = cx + (radius - 24) * Math.cos(rad);
            const y2 = cy + (radius - 24) * Math.sin(rad);
            const major = i === 0 || i === 5 || i === 10;
            return (
              <line key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="rgba(255,255,255,0.18)"
                strokeWidth={major ? 1.5 : 1}
                strokeLinecap="round"
              />
            );
          })}
          {/* Progress arc */}
          <path
            d={pathForArc(cx, cy, radius, startAngle, valueAngle)}
            fill="none"
            stroke={color}
            strokeWidth="14"
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${color}66)` }}
          />
        </svg>

        {/* Center label overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span
            className="font-display font-extrabold text-white tabular-nums tracking-tight leading-none"
            style={{ fontSize: Math.round(size * 0.32), color }}
          >
            {clamped}
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/55 mt-1">
            {t("v3.guardian_score.label")}
          </span>
          <span
            className="mt-2 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.16em] border"
            style={{ borderColor: `${color}66`, color, background: `${color}10` }}
          >
            {t(`v3.guardian_score.band.${band}`)}
          </span>
        </div>
      </div>

      {/* Component mini-bars (coverage / deadline_health / critical_health / register / covenant) */}
      {components && (
        <ul className="grid grid-cols-1 gap-2 w-full max-w-[260px] mt-2" data-testid="guardian-score-components">
          {Object.entries(components).slice(0, 3).map(([k, v]) => (
            <li key={k} className="flex items-center gap-2 text-[11px] text-white/65">
              <span className="w-20 shrink-0 truncate text-white/55" title={t(`v3.guardian_score.component.${k}`, { defaultValue: k })}>
                {t(`v3.guardian_score.component.${k}`, { defaultValue: k })}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${v}%`, background: color, opacity: 0.85 }} />
              </div>
              <span className="tabular-nums w-8 text-right text-white/75">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
